/* prettier-ignore */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, ViewChild } from '@angular/core';
import { ChatInputComponent } from './chatInput/chatInput.component';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DataChatService } from 'src/chatbot/service/dataChat.service';
import { SendButtonComponent } from './buttons/sendButton/sendButton/sendButton.component';
import { CancelButtonComponent } from './buttons/cancelButton/cancelButton.component';
import { SendMessageService } from 'src/chatbot/service/sendMessage.service';
import { TranslateModule } from '@ngx-translate/core';
import { InfoIconComponent } from 'src/chatbot/icons/info-icon.component';
import { LinkIconComponent } from 'src/chatbot/icons/link-icon.component';
import { VoiceButtonComponent } from './buttons/voiceButton/voiceButton.component';
import { ModalController } from '@ionic/angular';
import { ChatBotInfoComponent } from '../chatBotInfo/chatBotInfo.component';
import { RecordAudioHandlerService } from 'src/chatbot/service/recordAudioHandler.service';
import { WebSocketService } from 'src/chatbot/service/webSocket.service';
import { PlayAudioHandlerService } from 'src/chatbot/service/playAudioHandler.service';
import { LocalFillerPhrasesService } from 'src/chatbot/service/localFillerPhrases.service';
import { environment } from 'src/environments/environment';
import { TranslationService } from 'src/app/services/translation.service';

@Component({
  selector: 'chat-input-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ChatInputComponent,
    SendButtonComponent,
    CancelButtonComponent,
    InfoIconComponent,
    LinkIconComponent,
    VoiceButtonComponent
  ],
  templateUrl: './chatInputForm.component.html',
  styleUrl: './chatInputForm.component.scss',
  changeDetection: ChangeDetectionStrategy.Default,
})

export class ChatInputFormComponent {
  @ViewChild('waveformCanvas', { static: true }) waveformCanvas!: ElementRef<HTMLCanvasElement>;
  
  private webSocketService = inject(WebSocketService)
  private recordService = inject(RecordAudioHandlerService);
  private playService = inject(PlayAudioHandlerService);
  private phrasesFiller = inject(LocalFillerPhrasesService);
  
  private modalController = inject(ModalController)
  private fb = inject(FormBuilder);
  public dataChat = inject(DataChatService);
  public sendMessage = inject(SendMessageService);
  public environment = environment;
  public chatForm: FormGroup;

  public isRecording = computed(() => this.recordService.isRecording());
  public isStreaming = computed(() => this.dataChat.isStreaming());
  public isPlaying = computed(() => this.dataChat.isPlaying());
  public isPlayingWelcome = computed(() => this.phrasesFiller.isPlayingWelcome());
  public allowSendAudio = computed(() => this.dataChat.allowSendAudio());
  /** CMAD-231: disparador del aviso (retorno de foco al cerrar el diálogo) y su estado aria-expanded */
  @ViewChild('infoTrigger', { static: true }) infoTrigger!: ElementRef<HTMLButtonElement>;
  private translation = inject(TranslationService);
  public isInfoOpen = signal(false);
  private modal: any;

  constructor() {
    this.chatForm = this.fb.group({
      userInput: ['', Validators.required],
    })
  }
  
  handleSubmit() {
    if (this.isStreaming())
      return;
    
    const selectedImage = this.dataChat.selectedImage();
    const text = this.chatForm.value.userInput;

    if (!selectedImage && (!text || text.trim() === "")){
      this.chatForm.get('userInput')?.setValue(null);
      return;
    }

    if (selectedImage) {
      console.log("Sending selected image")
      this.sendMessage.sendImageMessage(selectedImage, text);
      this.dataChat.selectedImage.set(null);
      this.dataChat.imagePreviewUrl.set(null);
    } else {
      this.sendMessage.sendMessageWebSocket(text);
    }

    this.chatForm.reset();
    this.dataChat.inputText.set(null)
  }

  handleReceivedData(event) {
    switch (event) {
      case 'START':
        this.handleStart();
        break;
      case 'STOP':
        this.handleRecordingStop();
        break;
    }
  }

  /**
   * CMAD-231 (WCAG 4.1.2 / 2.4.3): diálogo de información como diálogo modal accesible.
   * - Sin 'ion-disable-focus-trap': la trampa de foco de Ionic devuelve el foco al diálogo
   *   (antes se escapaba a la página en el segundo Tab).
   * - Nombre accesible vía htmlAttributes: Ionic HEREDA 'aria-label' (y 'role') al wrapper del
   *   shadow root (.modal-wrapper), que ya lleva role="dialog" + aria-modal="true". aria-labelledby
   *   no se hereda y un IDREF no cruza el shadow boundary, por eso no se usa. El host <ion-modal>
   *   queda sin role/aria-label a propósito: duplicarlos anidaría dos diálogos.
   * - Al cerrar, Ionic restaura el foco al elemento activo en el present(); el fallback cubre los
   *   navegadores que no enfocan un <button> al hacer clic (Safari / Firefox en macOS).
   */
  async openModal() {
    if (this.modal)
      return;

    this.isInfoOpen.set(true);
    try {
      this.modal = await this.modalController.create({
        component: ChatBotInfoComponent,
        cssClass: ['chatBotModal'],
        componentProps: {},
        htmlAttributes: { 'aria-label': this.translation.instant('Información sobre el chatbot') },
      });
      await this.modal.present();
      await this.modal.onDidDismiss();
    } finally {
      this.modal = null;
      this.isInfoOpen.set(false);
      const active = document.activeElement;
      if (!active || active === document.body)
        this.infoTrigger?.nativeElement.focus();
    }
  }

  goToiUrban() {
    window.open('https://iurban.es/', "_blank");
  }

  handleStart() {
    const waves = this.waveformCanvas?.nativeElement;
    this.recordService.startRecording(waves);
  }

  getCanvasWidth(form) {
    return (this.isRecording() && !this.isPlayingWelcome())
    ? form.offsetWidth - 124
    : form.offsetWidth - 100;
  }

  handleStreamingStop() {
    this.dataChat.abortSignal().abort();
    this.webSocketService.cancelCompletion();
  }

  handleRecordingStop() {
    // this.webSocketService.enableLoader();
    this.recordService.stopRecording()
  }

  handleStopPlaying() {
    this.dataChat.abortSignal().abort();
    this.webSocketService.cancelCompletion();
    this.playService.stopNow();
  }

}
